<template>
  <div>
    <b-dropdown
      text="Create backup"
      variant="primary"
      class="create-backup-dropdown"
      right
      :disabled="disabled"
    >
      <b-dropdown-item @click="selectSource('local')">
        <feather-icon
          icon="DatabaseIcon"
          class="mr-50"
        />
        <span>Local</span>
      </b-dropdown-item>
      <b-dropdown-item @click="selectSource('remote')">
        <feather-icon
          icon="DownloadCloudIcon"
          class="mr-50"
        />
        <span>Remote</span>
      </b-dropdown-item>
    </b-dropdown>

    <b-modal
      ref="modalControllerOffline"
      title="The controller is offline"
      centered
      :ok-only="source === 'remote'"
      :ok-title="source === 'remote' ? 'Ok' : 'Continue'"
      @ok="isOfflineConfirmed = source === 'local'"
      @hidden="onOfflineModalHidden"
    >
      <p
        v-if="source === 'remote'"
        class="mb-0"
      >
        A Remote backup is read from the controller, so it cannot be created while the controller is offline.
        You can create a Local backup from the settings stored in NetPanel instead.
      </p>
      <p
        v-else
        class="mb-0"
      >
        The backup will be built from the settings stored in NetPanel.
        They may differ from the settings on the controller, because changes are not applied while it is offline.
      </p>
    </b-modal>

    <b-modal
      ref="modalCreateBackup"
      :title="`Create ${sourceLabel} backup`"
      ok-title="Create"
      centered
      :ok-disabled="isSubmitting || !title.trim()"
      @ok.prevent="submit"
    >
      <p class="mb-1">
        {{ sourceDescription }}
      </p>
      <b-form-group
        label="Title"
        label-for="site-backup-title"
      >
        <b-form-input
          id="site-backup-title"
          v-model="title"
          maxlength="255"
          :state="title.trim() ? null : false"
          @keyup.enter="submit"
        />
      </b-form-group>
    </b-modal>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import {
  BDropdown, BDropdownItem, BModal, BFormGroup, BFormInput,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { sourceLabels } from '../../site-backups/helpers'

const sourceDescriptions = {
  local: 'The backup will be built from the settings stored in NetPanel, including WAN. DHCP Lease Reservations are only in Remote backups.',
  remote: 'The backup will be read from the controller, the same way Pull does, including WAN and DHCP Lease Reservations. '
    + 'Nothing is saved to the site settings.',
}

const pad = value => String(value).padStart(2, '0')

export default {
  components: {
    BDropdown,
    BDropdownItem,
    BModal,
    BFormGroup,
    BFormInput,
  },
  props: {
    siteId: {
      required: true,
      type: [String, Number],
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    isControllerOnline: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      source: 'local',
      title: '',
      isSubmitting: false,
      isOfflineConfirmed: false,
    }
  },
  computed: {
    sourceLabel() {
      return sourceLabels[this.source]
    },
    sourceDescription() {
      return sourceDescriptions[this.source]
    },
  },
  methods: {
    ...mapActions('siteBackups', ['createItem']),
    selectSource(source) {
      this.source = source

      if (this.isControllerOnline) {
        this.openModal()
      } else {
        this.$refs.modalControllerOffline.show()
      }
    },
    // The title modal opens only after this one is gone, so the two never overlap.
    onOfflineModalHidden() {
      if (this.isOfflineConfirmed) {
        this.isOfflineConfirmed = false
        this.openModal()
      }
    },
    openModal() {
      const { source } = this
      const now = new Date()

      this.title = `${sourceLabels[source]} backup ${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`
      this.$refs.modalCreateBackup.show()
    },
    async submit() {
      if (this.isSubmitting || !this.title.trim()) {
        return
      }

      this.isSubmitting = true

      try {
        await this.createItem({ siteId: this.siteId, source: this.source, title: this.title.trim() })
        this.$refs.modalCreateBackup.hide()
        this.$emit('created')
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Backup scheduled',
            text: 'It will appear as Done in the Backups tab once it is created.',
            icon: 'BellIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        console.warn(error)
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';

// InfoPage has an unscoped `.dropdown button` rule that makes every dropdown toggle transparent.
.create-backup-dropdown::v-deep .dropdown-toggle {
  padding: 0.786rem 1.5rem;
  border-color: $primary !important;
  background-color: $primary !important;
  color: $white !important;
}
</style>
